import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta } from '../../web/ReactQueryProvider';

interface IDailyActivityRole {
	creator?: { id?: string };
	role?: string;
}

/**
 * Whether an admin's role today is MGR, for Hours Review's default to their reports. Admin-only: a non-admin is already scoped to
 * their own days and their reports' by the API, and the summary this reads is admin-only. Only a default, never access: MGR is
 * self-assignable.
 */
export function useIsMgrRole({ enabled = true }: { enabled?: boolean } = {}) {
	const userSession = useUserSession();
	const userId = userSession.user?.id;

	const { data, isLoading } = useQuery({
		queryKey: ['userDailyActivity', 'mgrRole', userId],
		queryFn: async (): Promise<IDailyActivityRole | null> => {
			// The summary maps each bound from a UTC instant to the user's local day, so send now rather than a date (a bare date
			// reads as UTC midnight, which is yesterday anywhere west of UTC). The caller's own day would be read-or-create.
			const now = encodeURIComponent(new Date().toISOString());
			const summary = await userSession.webServiceHelper.callWebServiceAsync<Api.IPagedCollection<IDailyActivityRole>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `userDailyActivity/summary?userId=${encodeURIComponent(userId ?? '')}&startDate=${now}&endDate=${now}`,
				}),
				'GET'
			);
			return summary.value?.values?.find(activity => activity.creator?.id === userId) ?? null;
		},
		enabled: enabled && !!userId,
		staleTime: 5 * 60 * 1000,
		// A failure retries rather than caching "not MGR"; once retries are spent the default falls back to every manager, which the
		// picker can still change, so it logs without a toast.
		meta: {
			errorLogging: { action: 'IsMgrRole-Error', category: 'TimeEntry' },
		} satisfies LevStandardQueryMeta,
	});

	return { isLoading, isMgr: data?.role === 'MGR' };
}
